'use client'

import type { Maybe } from '@/frontend-types'
import { formatDate } from '@/helpers/date'
import type { FC } from 'react'
import { useState } from 'react'
import { FontAwesomeIcon } from '../atoms/FontAwesomeIcon'
import { linkIconVariants } from '../atoms/LinkIcon'

type CopyState = 'idle' | 'copied' | 'error'

export type NewsDateShareBlockProps = {
	label: string
	date?: Maybe<string>
	readTime?: Maybe<string>
}

export const NewsDateShareBlock: FC<NewsDateShareBlockProps> = ({ date, readTime, label }) => {
	const [copyState, setCopyState] = useState<CopyState>('idle')

	const shareOnClick = async () => {
		try {
			await navigator.share({
				title: document.title,
				url: window.location.href,
			})
		} catch (error) {
			if (error instanceof Error && error.name !== 'AbortError') {
				const url = window.location.href
				try {
					await navigator.clipboard.writeText(url)
					setCopyState('copied')
				} catch {
					setCopyState('error')
				}
			}
		}
	}

	return (
		<div className="flex w-full flex-col gap-x-xl tablet-p:flex-row tablet-p:justify-between">
			<div className="flex flex-row items-center gap-x-lg text-body-sm">
				{date && <span>{formatDate(date)}</span>}
				{readTime && <span className="font-semibold">{readTime}</span>}
			</div>
			{copyState === 'idle' ? (
				<button className={linkIconVariants({ intent: 'primary' })} onClick={shareOnClick}>
					<span>{label}</span>
					<FontAwesomeIcon icon={['fas', 'share']} size="md" />
				</button>
			) : (
				<div className={linkIconVariants({ intent: 'primary' })}>
					<span>{copyState === 'copied' ? 'Copied to clipboard' : 'Unable to share'}</span>
					<FontAwesomeIcon
						icon={['fas', copyState === 'copied' ? 'clipboard-check' : 'exclamation-triangle']}
						size="md"
					/>
				</div>
			)}
		</div>
	)
}

export default NewsDateShareBlock
